import { useEffect, useState } from "react";
import { getSignedUrl } from "../services/Firebase";

// Session caches keyed by storage path: revisits and kit switches reuse the
// URL and the browser's decoded image instead of fetching again.
const pending = new Map<string, Promise<string>>();
const loaded = new Map<string, string>();

function loadImage(path: string): Promise<string> {
  let promise = pending.get(path);
  if (!promise) {
    promise = getSignedUrl(path).then(
      (url) =>
        new Promise<string>((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            loaded.set(path, url);
            resolve(url);
          };
          image.onerror = () => reject(new Error(`Image failed to load: ${path}`));
          image.src = url;
        }),
    );
    // A failure shouldn't stick for the whole session.
    promise.catch(() => pending.delete(path));
    pending.set(path, promise);
  }
  return promise;
}

/**
 * URLs for every path once all of them have downloaded, so a set of images
 * can appear together. Null while any is loading, or if any fails.
 */
export function useLoadedImageUrls(paths: string[]): string[] | null {
  const key = paths.join("\n");
  const [result, setResult] = useState<{ key: string; urls: string[] } | null>(
    null,
  );
  useEffect(() => {
    if (!key) return;
    let current = true;
    Promise.all(key.split("\n").map(loadImage)).then(
      (urls) => {
        if (current) setResult({ key, urls });
      },
      () => {},
    );
    return () => {
      current = false;
    };
  }, [key]);
  if (!paths.length) return null;
  if (paths.every((path) => loaded.has(path)))
    return paths.map((path) => loaded.get(path)!);
  return result?.key === key ? result.urls : null;
}
